/**
 * The quick-start checklist (MB's R125, decision 830; spec docs/specs/project-reporting/quick-start-checklist.md): before
 * the first flight, whether the project is set up correctly. Each check gives Pass, Warning or Fail with a word, the
 * problems in plain words and where they are fixed. Every check reads the project's existing data and is built from
 * the rules that already exist (the zone from the flight plan, what a daily entry needs, the aircraft's validation,
 * the crew on a day, the height specs, the flight-plan files); none is written twice.
 *
 * The batch 6 wizards (F13, F26) reuse these checks: `QUICK_START_CHECKS` lists them with their sections, and each
 * check is a function of the same `ChecklistInput`, so a wizard step can run its own section only (spec §9).
 */
import { crewOnDate } from './crew-dates.js';
import { stationLinkOn, type StationLinkRow } from './diurnal-cloud-view.js';
import { expectedEpsg, utmZoneName } from './geosoft-projection.js';
import { blockFolderOf as blockFolderIn, geosoftZoneFixes, type GeosoftZoneFix } from './qc-daily.js';
import type { GeosoftBlocksPlan } from './geosoft-block-templates.js';
import { geosoftBlockDatabases, type BlockDatabasesFix } from './geosoft-block-databases.js';
import { qcFamilyForAircraft, projectQcRules } from './geosoft-qc.js';
import type { Aircraft, Block, Person, PlannedLine, Position, Project, ProjectCrew } from './model.js';
import { findBlockPlanFiles } from './plan-files.js';
import { dailyEntrySetupGaps, validateAircraft } from './project-setup.js';
import {
  crewHeightsWarning,
  distanceRuleLimits,
  formatNumber,
  qcRule,
  type DistanceRuleKey,
} from './qc-spec.js';
import { holdsRole, isActivePerson } from './staff.js';

export type ChecklistState = 'pass' | 'warn' | 'fail';
export type ChecklistSection = 'project' | 'staff' | 'aircraft' | 'basemag' | 'alerts' | 'files';

/** The word paired with each state's colour (brand book: Pass, Warning, Fail). */
export const CHECKLIST_STATE_WORD: Readonly<Record<ChecklistState, string>> = {
  pass: 'Pass',
  warn: 'Warning',
  fail: 'Fail',
};

export const CHECKLIST_SECTION_LABEL: Readonly<Record<ChecklistSection, string>> = {
  project: 'Project',
  staff: 'Staff',
  aircraft: 'Aircraft',
  basemag: 'Basemag',
  alerts: 'Alerts',
  files: 'Files',
};

/**
 * Where a check's problem is fixed, by place rather than screen: each app maps a place to its own screen (spec §4),
 * so a Teslyn Cloud wizard can send the person to its own step instead.
 */
export type ChecklistPlace =
  | 'settings.blocks'
  | 'settings.qc_limits'
  | 'settings.staff'
  | 'settings.crew'
  | 'settings.aircraft'
  | 'settings.positions'
  | 'qc.settings'
  | 'qc'
  | 'diurnal'
  | 'planning'
  | 'cloud.stations'
  | 'cloud.alerts'
  /** Teslyn Cloud, Aircraft, Geosoft templates: where a type's template is uploaded (MB, 4 Nov 2026). */
  | 'cloud.geosoft_templates';

export type ChecklistItemId =
  | 'project.zone'
  | 'project.heights'
  | 'project.qc_settings'
  | 'staff.register'
  | 'staff.crew'
  | 'aircraft.register'
  | 'aircraft.tracker'
  | 'basemag.position'
  | 'basemag.station'
  | 'basemag.diurnal'
  | 'alerts.operators'
  | 'alerts.push'
  | 'files.flight_plan'
  | 'files.boundaries'
  | 'files.geosoft'
  | 'files.geosoft_databases'
  | 'files.geosoft_zone'
  | 'files.geosoft_templates';

export interface ChecklistItem {
  readonly id: ChecklistItemId;
  readonly section: ChecklistSection;
  readonly title: string;
  readonly state: ChecklistState;
  /** `CHECKLIST_STATE_WORD[state]`. */
  readonly word: string;
  /** What is wrong, one sentence each; empty on a pass. */
  readonly problems: readonly string[];
  /** What was found, one line (shown on a pass, and beside the problems otherwise). */
  readonly found: string;
  /** Where it is fixed. */
  readonly fix: ChecklistPlace;
  /**
   * Decisions 847 and 939: what "Set the zone" can do, one press per zone, each database taking its own block's zone
   * (the Geosoft zone check only).
   */
  readonly zoneFixes?: readonly GeosoftZoneFix[];
  /** R146: what "Update the blocks" and the kept files' buttons work on (the Geosoft templates check only). */
  readonly blockTemplates?: GeosoftBlocksPlan;
  /** R164: what "Create the databases" makes, one entry per block (the lines and boundary databases check only). */
  readonly blockDatabases?: readonly BlockDatabasesFix[];
}

/** A Geosoft database's coordinate system as `project_files_check` read it (teslyn_geosoft::projection). */
export interface GeosoftDatabaseFacts {
  /** `Flt02.gdb`. */
  readonly name: string;
  readonly bytes: number;
  /** Its coordinate system, or null when it has none (or it was not read: `read` false). */
  readonly projection: { readonly name: string; readonly epsg: number | null } | null;
  /** False when it was not read: no `.gdb.xml` beside it and too large to scan. */
  readonly read: boolean;
}

/** One block folder's Geosoft project. */
export interface GeosoftFolderFacts {
  /** The block folder, `F26048_Milgun`. */
  readonly block_folder: string;
  /** The `.gpf` files, relative to the block folder (`F26048.gpf`, `Geosoft/F26048.gpf`). */
  readonly projects: readonly string[];
  /** Every `.gdb` in `Geosoft/Database`. */
  readonly databases: readonly GeosoftDatabaseFacts[];
  /** Decision 939: the zone in this block folder's own lines file, read from the file; absent or null when none. */
  readonly lines_epsg?: number | null;
  /** R164: the `.map` files in its `Geosoft\Maps`; absent where not listed. */
  readonly maps?: readonly string[];
}

/** What `project_files_check` found in the project folder (spec §6). */
export interface ProjectFilesFacts {
  readonly folder: string | null;
  readonly folder_exists: boolean;
  /** Files directly in each `<block folder>/1. Flight Plan/`, relative to the project folder, `/` separated. */
  readonly plan_files: readonly string[];
  readonly geosoft: readonly GeosoftFolderFacts[];
}

/** What Teslyn Cloud said, read with the person's sign-in; `state` says why nothing was read. */
export type ChecklistCloud =
  | { readonly state: 'signed_out' }
  | { readonly state: 'local' }
  | { readonly state: 'offline' }
  | {
      readonly state: 'ok';
      /** The project's `project_base_station` rows. */
      readonly stationLinks: readonly Pick<StationLinkRow, 'station_id' | 'starts_on' | 'ends_on'>[];
      /**
       * The person's devices that receive their push alerts: their browsers (`push_subscription`, own rows) and
       * their phones signed in to Teslyn Mobile with alerts on (`mobile_device` rows with a push token, not
       * expired, not forgotten; a retired duplicate of a phone is forgotten too, so it never counts).
       */
      readonly pushDevices: number;
    };

export interface ChecklistInput {
  readonly project: Project;
  /** Today, `YYYY-MM-DD`, the laptop's calendar date (`localIsoDate`). */
  readonly today: string;
  readonly blocks: readonly Block[];
  readonly plannedLines: readonly Pick<PlannedLine, 'block_id' | 'deleted_at'>[];
  readonly aircraft: readonly Aircraft[];
  readonly people: readonly Person[];
  readonly crew: readonly ProjectCrew[];
  readonly positions: readonly Position[];
  /** The organisation's tracker devices; one whose `aircraft_id` is an aircraft tracks it. */
  readonly trackers: readonly { readonly aircraft_id: string | null; readonly deleted_at: string | null }[];
  /** The project folder's files; null where the folder cannot be seen (Teslyn Cloud). */
  readonly files: ProjectFilesFacts | null;
  /** R146: each block's Geosoft templates against Teslyn's; null or absent where it cannot be known (Teslyn Cloud). */
  readonly geosoftTemplates?: GeosoftBlocksPlan | null;
  /** Oasis montaj found on this laptop; null where it cannot be known (Teslyn Cloud). */
  readonly geosoftInstalled: boolean | null;
  /** Diurnal QC running for the project in this Teslyn Field (decision 810); null where it cannot be known. */
  readonly diurnalRunning: boolean | null;
  /** The organisation's TA Live units (the bundled tenant list). */
  readonly taLiveUnits: number;
  readonly cloud: ChecklistCloud;
}

type Check = (input: ChecklistInput) => ChecklistItem;

// ------------------------------------------------------------------------------------------------------- helpers

const live = <T extends { readonly deleted_at: string | null }>(rows: readonly T[]): T[] =>
  rows.filter((r) => r.deleted_at === null);

/** "A", "A and B", "A, B and C". */
export function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]!}`;
}

/** "Flt02.gdb, Flt03.gdb and 25 more" for long lists. */
function someNames(names: readonly string[], shown = 4): string {
  if (names.length <= shown + 1) return listWords(names);
  return `${names.slice(0, shown).join(', ')} and ${names.length - shown} more`;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

function item(
  id: ChecklistItemId,
  section: ChecklistSection,
  title: string,
  fix: ChecklistPlace,
  fails: readonly string[],
  warns: readonly string[],
  found: string,
): ChecklistItem {
  const state: ChecklistState = fails.length > 0 ? 'fail' : warns.length > 0 ? 'warn' : 'pass';
  return {
    id,
    section,
    title,
    state,
    word: CHECKLIST_STATE_WORD[state],
    problems: [...fails, ...warns],
    found,
    fix,
  };
}

const surveyBlocks = (blocks: readonly Block[]): Block[] => live(blocks).filter((b) => b.kind === 'survey');

/** The short zone, `50S`, of a WGS 84 / UTM EPSG code; the code itself for anything else. */
function zoneShort(epsg: number): string {
  return utmZoneName(epsg)?.replace('WGS 84 / UTM zone ', '') ?? `EPSG ${epsg}`;
}

const personName = (people: readonly Person[], c: ProjectCrew): string =>
  people.find((p) => p.id === c.person_id)?.name ?? c.person_name ?? 'Someone removed from the register';

const SIGN_IN_WORDS: Readonly<Record<Exclude<ChecklistCloud['state'], 'ok'>, string>> = {
  signed_out: 'Not checked: sign in to Teslyn Cloud to check it.',
  local: 'Not checked: the project is not in Teslyn Cloud yet.',
  offline: 'Not checked: Teslyn Cloud cannot be reached now.',
};

const FIELD_ONLY = 'Checked in Teslyn Field, on the laptop that holds the project folder.';

/** The block folder of a block in a Thomson project folder: `<job>_<block key>` (decision 189). */
function blockFolderOf(project: Pick<Project, 'number'>, block: Pick<Block, 'key'>): string {
  return `${project.number}_${block.key}`.toLowerCase();
}

// -------------------------------------------------------------------------------------------------------- project

/** The projection zone from the flight plan (R26): every survey block's lines file gives one and they agree. */
export const checkProjectionZone: Check = ({ project, blocks }) => {
  const survey = surveyBlocks(blocks);
  const fails: string[] = [];
  const warns: string[] = [];
  if (survey.length === 0)
    return item(
      'project.zone',
      'project',
      'Projection zone from the flight plan',
      'settings.blocks',
      ['No survey blocks: the flight-plan files in the project folder have not been read.'],
      [],
      'No survey blocks.',
    );
  const noZone = survey.filter((b) => utmZoneName(b.epsg) === null).map((b) => b.name);
  if (noZone.length > 0)
    fails.push(
      `${listWords(noZone)} ${noZone.length === 1 ? 'has' : 'have'} no zone: the lines file's first line gives it (Zone=50 Hemi=S).`,
    );
  const zones = new Map<number, string[]>();
  for (const b of survey)
    if (utmZoneName(b.epsg) !== null) zones.set(b.epsg!, [...(zones.get(b.epsg!) ?? []), b.name]);
  // decision 939: blocks in different zones are not a problem in themselves (files.geosoft_zone compares each block)
  const expected = expectedEpsg(survey);
  if (expected !== null && project.epsg != null && project.epsg !== expected)
    warns.push(
      `The project's own zone is ${zoneShort(project.epsg)}, but the flight plan puts the survey in ${zoneShort(expected)}: positions taken from a GPS file use the project's zone.`,
    );
  const found =
    expected !== null
      ? `${utmZoneName(expected)!} from the lines files of ${survey.length === 1 ? 'the survey block' : `all ${survey.length} survey blocks`}.`
      : zones.size > 1
        ? 'Each block in the zone of its own lines file.'
        : 'No single zone.';
  return item(
    'project.zone',
    'project',
    'Projection zone from the flight plan',
    'settings.blocks',
    fails,
    warns,
    found,
  );
};

const RULE_TITLE: Readonly<Record<DistanceRuleKey, string>> = {
  terrain_clearance: 'terrain clearance',
  flight_path: 'flight path',
};

/** The height specs and QC limits (decision 822): each survey block's survey height, the distance rules. */
export const checkHeightSpecs: Check = ({ project, blocks, aircraft }) => {
  const survey = surveyBlocks(blocks);
  const fails: string[] = [];
  const warns: string[] = [];
  const unset = survey
    .filter((b) => !(b.terrain_clearance_m && b.terrain_clearance_m > 0))
    .map((b) => b.name);
  if (unset.length > 0)
    fails.push(
      `No survey height for ${listWords(unset)}: Flight data QC skips the terrain clearance check until it is set.`,
    );
  const rules = projectQcRules(project, live(aircraft)[0]?.type_name ?? '');
  const parts: string[] = [];
  for (const key of ['terrain_clearance', 'flight_path'] as const) {
    const limits = distanceRuleLimits(rules, key);
    const rule = qcRule(rules, key);
    if (!limits || !rule) {
      fails.push(
        `No ${RULE_TITLE[key]} limit: Flight data QC cannot check the contract's ${RULE_TITLE[key]} rule.`,
      );
      continue;
    }
    if (!rule.enabled)
      warns.push(`The ${RULE_TITLE[key]} check is switched off, so Flight data QC does not run it.`);
    parts.push(
      `${RULE_TITLE[key]} ±${formatNumber(limits.allowedM)} m${limits.overM !== null ? ` over ${formatNumber(limits.overM)} m` : ''}`,
    );
  }
  const heights = survey
    .filter((b) => b.terrain_clearance_m && b.terrain_clearance_m > 0)
    .map((b) => `${b.name} ${formatNumber(b.terrain_clearance_m!)} m`);
  const found = [heights.length > 0 ? `Survey height ${heights.join(', ')}` : null, ...parts]
    .filter((p): p is string => p !== null)
    .join('; ');
  return item(
    'project.heights',
    'project',
    'Height specs and QC limits',
    'settings.qc_limits',
    fails,
    warns,
    found ? `${found.charAt(0).toUpperCase()}${found.slice(1)}.` : 'No limits.',
  );
};

/** Flight data QC's own settings: the aircraft system, the crew's hard deck and lower working height. */
export const checkQcSettings: Check = ({ project, aircraft }) => {
  const warns: string[] = [];
  const family = project.qc_family ?? qcFamilyForAircraft(live(aircraft)[0]?.type_name, project.job_type);
  if (family === null)
    warns.push('No aircraft system chosen: Flight data QC cannot pick its scripts and channels.');
  const deck = project.qc_hard_deck_m ?? null;
  const lwh = project.qc_lower_working_m ?? null;
  if (deck === null || lwh === null) {
    const missing = [deck === null ? 'hard deck' : null, lwh === null ? 'lower working height' : null].filter(
      (w): w is string => w !== null,
    );
    warns.push(
      `The ${listWords(missing)} ${missing.length === 1 ? 'is' : 'are'} not set: the crew sets them after the safety recce flight.`,
    );
  }
  const swapped = crewHeightsWarning(deck, lwh);
  if (swapped) warns.push(swapped);
  const found = [
    family ? `Aircraft system ${family.toUpperCase()}` : null,
    deck !== null ? `hard deck ${formatNumber(deck)} m` : null,
    lwh !== null ? `lower working height ${formatNumber(lwh)} m` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(', ');
  return item(
    'project.qc_settings',
    'project',
    'Flight data QC settings',
    'qc.settings',
    [],
    warns,
    found ? `${found}.` : 'Nothing set.',
  );
};

// ---------------------------------------------------------------------------------------------------------- staff

/** Pilots and operators in the register: a flight's daily entry needs both (`dailyEntrySetupGaps`). */
export const checkStaffRegister: Check = ({ aircraft, people, positions }) => {
  const gaps = dailyEntrySetupGaps(aircraft, people, positions);
  const fails: string[] = [];
  if (gaps.includes('pilot'))
    fails.push('No active pilot in the staff register: a flight cannot be entered.');
  if (gaps.includes('operator'))
    fails.push('No active operator in the staff register: a flight with survey hours cannot be entered.');
  const active = people.filter(isActivePerson);
  const pilots = active.filter((p) => holdsRole(p, 'pilot')).length;
  const operators = active.filter((p) => holdsRole(p, 'operator')).length;
  return item(
    'staff.register',
    'staff',
    'Pilots and operators',
    'settings.staff',
    fails,
    [],
    `${plural(pilots, 'pilot')} and ${plural(operators, 'operator')} in the staff register.`,
  );
};

/** The project crew on today's date (decision 422): a pilot and an operator. */
export const checkCrewToday: Check = ({ crew, people, today }) => {
  const on = crewOnDate(
    crew.filter((c) => c.deleted_at === null),
    today,
  );
  const warns: string[] = [];
  const pilots = on.filter((c) => c.role === 'pilot');
  const operators = on.filter((c) => c.role === 'operator');
  if (pilots.length === 0) warns.push('No pilot on the project crew today.');
  if (operators.length === 0) warns.push('No operator on the project crew today.');
  const names = (rows: readonly ProjectCrew[]): string => listWords(rows.map((c) => personName(people, c)));
  const found =
    on.length === 0
      ? 'Nobody on the project crew today.'
      : [
          pilots.length > 0 ? `Pilots ${names(pilots)}` : null,
          operators.length > 0 ? `operators ${names(operators)}` : null,
        ]
          .filter((p): p is string => p !== null)
          .join('; ')
          .concat('.');
  return item('staff.crew', 'staff', 'Crew on the project today', 'settings.crew', [], warns, found);
};

// ------------------------------------------------------------------------------------------------------- aircraft

/** The aircraft: at least one, each passing the register's validation, with the figures MR and fuel need. */
export const checkAircraft: Check = ({ aircraft }) => {
  const list = live(aircraft);
  if (list.length === 0)
    return item(
      'aircraft.register',
      'aircraft',
      'Aircraft',
      'settings.aircraft',
      ['No aircraft: add the survey aircraft before its first flight is entered.'],
      [],
      'No aircraft.',
    );
  const fails: string[] = [];
  const warns: string[] = [];
  for (const a of list) {
    for (const issue of validateAircraft(a, list)) fails.push(`${a.registration}: ${issue.message}`);
    const missing = [
      a.fuel_flow_lph === null ? 'fuel flow' : null,
      a.mr_interval_h === null ? 'MR interval' : null,
      a.ttis_at_start_h === null ? 'TTIS at start' : null,
    ].filter((w): w is string => w !== null);
    if (missing.length > 0)
      warns.push(
        `${a.registration} has no ${listWords(missing)}: hours to the next MR and fuel are worked out from ${missing.length === 1 ? 'it' : 'them'}.`,
      );
  }
  return item(
    'aircraft.register',
    'aircraft',
    'Aircraft',
    'settings.aircraft',
    fails,
    warns,
    `${listWords(list.map((a) => (a.type_name ? `${a.registration} (${a.type_name})` : a.registration)))}.`,
  );
};

/** Each aircraft has a tracker device (decision 506): its live track, and the SOS and lost-tracking alerts. */
export const checkTrackers: Check = ({ aircraft, trackers }) => {
  const list = live(aircraft);
  const tracked = new Set(live(trackers).map((t) => t.aircraft_id));
  const without = list.filter((a) => !tracked.has(a.id)).map((a) => a.registration);
  const warns =
    without.length > 0
      ? [
          `${listWords(without)} ${without.length === 1 ? 'has' : 'have'} no tracker device: no live track on the map, and no SOS or lost-tracking alert.`,
        ]
      : [];
  const found =
    list.length === 0
      ? 'No aircraft.'
      : without.length === 0
        ? `${list.length === 1 ? 'The aircraft has' : `All ${list.length} aircraft have`} a tracker device.`
        : `${plural(list.length - without.length, 'aircraft', 'aircraft')} of ${list.length} tracked.`;
  return item(
    'aircraft.tracker',
    'aircraft',
    'Tracker on each aircraft',
    'settings.aircraft',
    [],
    warns,
    found,
  );
};

// -------------------------------------------------------------------------------------------------------- basemag

/** The base mag position: there is one (a daily entry needs it), with its coordinates. */
export const checkBaseMagPosition: Check = ({ positions }) => {
  const bases = live(positions).filter((p) => p.kind === 'base_mag');
  if (bases.length === 0)
    return item(
      'basemag.position',
      'basemag',
      'Base mag position',
      'settings.positions',
      ['No base mag position: a flight with survey hours cannot be entered.'],
      [],
      'No base mag position.',
    );
  const unplaced = bases.filter((p) => p.easting == null || p.northing == null).map((p) => p.name);
  const warns =
    unplaced.length > 0
      ? [
          `${listWords(unplaced)} ${unplaced.length === 1 ? 'has' : 'have'} no coordinates: take them from the base station's GPS file.`,
        ]
      : [];
  return item(
    'basemag.position',
    'basemag',
    'Base mag position',
    'settings.positions',
    [],
    warns,
    `${listWords(bases.map((p) => p.name))}.`,
  );
};

/** A TA Live unit linked to the project today (decision 788), when the organisation has units. */
export const checkStationLinked: Check = ({ taLiveUnits, cloud, today }) => {
  const title = 'Base station linked to the project';
  if (taLiveUnits === 0)
    return item(
      'basemag.station',
      'basemag',
      title,
      'cloud.stations',
      [],
      [],
      'Your organisation has no TA Live units: base mag files are imported on the Basemag files tab of Diurnal QC.',
    );
  if (cloud.state !== 'ok')
    return item(
      'basemag.station',
      'basemag',
      title,
      'cloud.stations',
      [],
      [SIGN_IN_WORDS[cloud.state]],
      'Not checked.',
    );
  const on = cloud.stationLinks.filter((l) => stationLinkOn(l, today));
  const warns =
    on.length === 0
      ? [
          'No TA Live unit is linked to the project today: Teslyn Cloud does not run diurnal QC or send station alerts for it. A manager links the unit in Teslyn Cloud, Station health.',
        ]
      : [];
  return item(
    'basemag.station',
    'basemag',
    title,
    'cloud.stations',
    [],
    warns,
    on.length === 0 ? 'No unit linked today.' : `${plural(on.length, 'TA Live unit')} linked today.`,
  );
};

/** Diurnal QC running for the project on this laptop (decision 810: once opened it keeps running). */
export const checkDiurnalRunning: Check = ({ diurnalRunning }) => {
  const title = 'Diurnal QC running';
  if (diurnalRunning === null)
    return item('basemag.diurnal', 'basemag', title, 'diurnal', [], [FIELD_ONLY], 'Not checked.');
  return item(
    'basemag.diurnal',
    'basemag',
    title,
    'diurnal',
    [],
    diurnalRunning
      ? []
      : [
          'Diurnal QC is not running for this project: open it once and it keeps watching the base station, with its alerts and alarm, behind the other screens.',
        ],
    diurnalRunning ? 'Running behind the other screens.' : 'Not running.',
  );
};

// --------------------------------------------------------------------------------------------------------- alerts

/**
 * Who receives Teslyn Cloud's alerts: the operators on the crew that day (decision 53), never pilots. Crew dates
 * decide (decision 919, R153): with no operator on the crew the organisation's managers receive them, and an operator
 * whose crew row has no end date keeps receiving them at home on break, so each one is a warning.
 */
export const checkAlertOperators: Check = ({ crew, people, today }) => {
  const rows = crew.filter((c) => c.deleted_at === null && c.role === 'operator');
  const operators = crewOnDate(rows, today);
  const warns: string[] = [];
  if (operators.length === 0)
    warns.push(
      "No operator on the project crew today: Teslyn Cloud sends the alerts to the organisation's managers instead.",
    );
  const open = rows.filter((c) => c.end_date === null);
  const openNames = [...new Set(open.map((c) => personName(people, c)))];
  if (openNames.length > 0)
    warns.push(
      `${listWords(openNames)} ${openNames.length === 1 ? 'has' : 'have'} no end date on the project crew: alerts keep reaching ${openNames.length === 1 ? 'them' : 'them all'} at home on break. Set the day ${openNames.length === 1 ? 'they leave' : 'each leaves'} in Crew on this project.`,
    );
  return item(
    'alerts.operators',
    'alerts',
    'Who receives the alerts',
    'settings.crew',
    [],
    warns,
    operators.length === 0
      ? "The organisation's managers (no operator on the crew today)."
      : `${listWords(operators.map((c) => personName(people, c)))}.`,
  );
};

/** What the checklist says when no phone or browser of the person receives their push alerts. */
export const ALERT_PUSH_NO_DEVICE_WORDS =
  'No phone or browser of yours receives your push alerts. Open Teslyn Mobile on your phone, Alerts, and press "Turn on alerts on this phone"; or in a browser, Teslyn Cloud, Alerts, "Turn on push on this device".';

/**
 * The signed-in person has a device that receives their push alerts: a phone with Teslyn Mobile's alerts on, or a
 * browser with push on (Teslyn Cloud, Alerts). Counts the person's live rows only: a phone that was replaced by its
 * own reinstall, signed out or removed from the list is forgotten and never counts (MB, 4 Nov 2026).
 */
export const checkAlertPush: Check = ({ cloud }) => {
  const title = 'Alerts on your phone';
  if (cloud.state !== 'ok')
    return item(
      'alerts.push',
      'alerts',
      title,
      'cloud.alerts',
      [],
      [SIGN_IN_WORDS[cloud.state]],
      'Not checked.',
    );
  const warns = cloud.pushDevices === 0 ? [ALERT_PUSH_NO_DEVICE_WORDS] : [];
  return item(
    'alerts.push',
    'alerts',
    title,
    'cloud.alerts',
    [],
    warns,
    cloud.pushDevices === 0 ? 'No device.' : `Push on ${plural(cloud.pushDevices, 'device')}.`,
  );
};

// ---------------------------------------------------------------------------------------------------------- files

/** The flight plan of each block folder (decision 189) and the planned lines read from it. */
export const checkFlightPlan: Check = ({ project, blocks, plannedLines, files }) => {
  const title = 'Flight plan and lines files';
  if (files === null)
    return item('files.flight_plan', 'files', title, 'planning', [], [FIELD_ONLY], 'Not checked.');
  if (!files.folder_exists)
    return item(
      'files.flight_plan',
      'files',
      title,
      'planning',
      [
        files.folder
          ? `The project folder ${files.folder} is not on this laptop.`
          : 'The project has no folder.',
      ],
      [],
      'No project folder.',
    );
  const fails: string[] = [];
  const warns: string[] = [];
  const plans = findBlockPlanFiles(files.plan_files, (p) => p);
  const withLines = plans.filter((p) => p.lines !== null);
  if (withLines.length === 0)
    fails.push(
      `No block folder has a lines file in its "1. Flight Plan" folder (<job>_<block>\\1. Flight Plan).`,
    );
  const survey = surveyBlocks(blocks);
  const folders = new Map(plans.map((p) => [p.folder.toLowerCase(), p]));
  const counts = new Map<string, number>();
  for (const l of live(plannedLines)) counts.set(l.block_id, (counts.get(l.block_id) ?? 0) + 1);
  const noLines = survey.filter((b) => (counts.get(b.id) ?? 0) === 0).map((b) => b.name);
  if (noLines.length > 0)
    fails.push(
      `${listWords(noLines)} ${noLines.length === 1 ? 'has' : 'have'} no planned lines: Line review and the daily km need them.`,
    );
  const known = new Set(live(blocks).map((b) => blockFolderOf(project, b)));
  for (const p of withLines) {
    const name = p.lines!.split('/').pop() ?? '';
    if (!/_lns\.lns$/i.test(name))
      warns.push(
        `${p.folder}'s flight plan has no _LNS.lns, only ${name}: the aircraft's Zdas loads the .lns.`,
      );
    if (p.folder !== '' && !known.has(p.folder.toLowerCase()))
      warns.push(`${p.folder} has a flight plan but is not a block of the project: it has not been read in.`);
  }
  for (const b of survey)
    if (!folders.get(blockFolderOf(project, b))?.lines)
      warns.push(`${b.name} has no lines file in ${project.number}_${b.key}\\1. Flight Plan.`);
  const lineCount = survey.reduce((n, b) => n + (counts.get(b.id) ?? 0), 0);
  return item(
    'files.flight_plan',
    'files',
    title,
    'planning',
    fails,
    warns,
    `${plural(withLines.length, 'flight plan')}, ${plural(survey.length, 'survey block')}, ${lineCount.toLocaleString('en-AU')} planned lines.`,
  );
};

/** Each survey block's boundary (the flight plan's `_BND.inp`), as the map, Windy and the hazard pack use it. */
export const checkBoundaries: Check = ({ project, blocks, files }) => {
  const title = 'Block boundaries';
  const survey = surveyBlocks(blocks);
  const warns: string[] = [];
  const noBoundary = survey.filter((b) => !b.boundary || b.boundary.length < 3).map((b) => b.name);
  if (noBoundary.length > 0)
    warns.push(
      `${listWords(noBoundary)} ${noBoundary.length === 1 ? 'has' : 'have'} no boundary: the map, the Windy boundary and the pilots' hazard pack leave ${noBoundary.length === 1 ? 'it' : 'them'} out.`,
    );
  if (files?.folder_exists) {
    const plans = new Map(
      findBlockPlanFiles(files.plan_files, (p) => p).map((p) => [p.folder.toLowerCase(), p]),
    );
    const noBnd = survey.filter((b) => {
      const p = plans.get(blockFolderOf(project, b));
      return p !== undefined && p.bnd === null;
    });
    for (const b of noBnd) warns.push(`${b.name}'s flight plan has no _BND.inp.`);
  }
  return item(
    'files.boundaries',
    'files',
    title,
    'planning',
    [],
    warns,
    `${plural(survey.length - noBoundary.length, 'survey block')} of ${survey.length} with a boundary.`,
  );
};

/**
 * The Geosoft project (`.gpf`) and Oasis montaj itself. Each block's lines database is `files.geosoft_databases`'s
 * (R164), named by block.
 */
/** Teslyn Field's own data folder in the project folder (`Teslyn`, `Survera` before decision 529): not a block's. */
const TESLYN_DATA_FOLDERS: ReadonlySet<string> = new Set(['teslyn', 'survera']);

export const checkGeosoftProject: Check = ({ files, geosoftInstalled }) => {
  const title = 'Geosoft project and databases';
  if (files === null) return item('files.geosoft', 'files', title, 'qc', [], [FIELD_ONLY], 'Not checked.');
  const fails: string[] = [];
  const warns: string[] = [];
  // Teslyn's own data folder (`Teslyn\geosoft`, its job folders; `Survera\` before the rename) is not a block's
  const folders = files.geosoft.filter((g) => !TESLYN_DATA_FOLDERS.has(g.block_folder.toLowerCase()));
  if (files.folder_exists && folders.length === 0)
    fails.push('No Geosoft folder in any block folder: Flight data QC finds the flight databases there.');
  if (folders.length > 0) {
    // MB (F26044, 29 Oct 2026): each block folder with a Geosoft folder and no project of its own is named
    const noGpf = folders.filter((g) => g.projects.length === 0).map((g) => g.block_folder);
    if (noGpf.length > 0)
      warns.push(
        `${listWords(noGpf)} ${noGpf.length === 1 ? 'has' : 'have'} a Geosoft folder but no Geosoft project (.gpf): Review line opens a flight's database in its block's project.`,
      );
  }
  if (geosoftInstalled === false)
    warns.push('Oasis montaj is not found on this laptop: Flight data QC and the weekly figures need it.');
  const dbs = folders.reduce((n, g) => n + g.databases.length, 0);
  const gpf = folders.flatMap((g) => g.projects.map((p) => `${g.block_folder}\\${p.replaceAll('/', '\\')}`));
  return item(
    'files.geosoft',
    'files',
    title,
    'qc',
    fails,
    warns,
    folders.length === 0
      ? 'No Geosoft folder.'
      : `${gpf.length > 0 ? someNames(gpf, 2) : 'No .gpf'}; ${plural(dbs, 'database')}.`,
  );
};

/**
 * R164: each survey block's lines and boundary databases, and "Create the databases" where they are missing
 * (geosoft-block-databases.ts holds where they live and what is made). A block without a lines database fails: Flight
 * data QC skips the flight path check for its lines. One without a boundary database warns: no boundary map.
 */
export const checkGeosoftDatabases: Check = ({ blocks, files }) => {
  const title = 'Lines and boundary databases in each block';
  if (files === null)
    return item('files.geosoft_databases', 'files', title, 'qc', [], [FIELD_ONLY], 'Not checked.');
  const plan = geosoftBlockDatabases(blocks, files);
  const fails: string[] = [];
  const warns: string[] = [];
  const name = (r: (typeof plan.rows)[number]): string => r.block_folder ?? r.block;
  const noLines = plan.rows.filter((r) => r.lines === null).map(name);
  const noBoundary = plan.rows.filter((r) => r.boundary === null).map(name);
  if (noLines.length > 0)
    fails.push(
      `${listWords(noLines)} ${noLines.length === 1 ? 'has' : 'have'} no lines database (Lines.gdb): Flight data QC skips the flight path check for ${noLines.length === 1 ? 'its' : 'their'} lines.`,
    );
  if (noBoundary.length > 0)
    warns.push(
      `${listWords(noBoundary)} ${noBoundary.length === 1 ? 'has' : 'have'} no boundary database (Boundary.gdb) and boundary map.`,
    );
  for (const r of plan.rows)
    if (r.cannot && (r.lines === null || r.boundary === null))
      warns.push(
        `${name(r)}: Teslyn cannot make ${r.lines === null ? 'its databases' : 'its boundary database'}: ${r.cannot}.`,
      );
  const both = plan.rows.filter((r) => r.lines !== null && r.boundary !== null).length;
  const found = item(
    'files.geosoft_databases',
    'files',
    title,
    'qc',
    fails,
    warns,
    plan.rows.length === 0
      ? 'No survey blocks.'
      : `${both} of ${plural(plan.rows.length, 'survey block')} with ${plan.rows.length === 1 ? 'its' : 'their'} lines and boundary databases.`,
  );
  return plan.fixes.length > 0 ? { ...found, blockDatabases: plan.fixes } : found;
};

/**
 * Every Geosoft database in the flight plan's zone (R26): a database stamped another zone (F26048's were 59S, the
 * survey is 50S) puts grids and maps in the wrong place. Read without Geosoft, from each database's `.gdb.xml`.
 */
export const checkGeosoftZone: Check = ({ blocks, files }) => {
  const title = "Geosoft databases in the flight plan's zone";
  if (files === null)
    return item('files.geosoft_zone', 'files', title, 'qc', [], [FIELD_ONLY], 'Not checked.');
  const dbs = files.geosoft.flatMap((g) => g.databases);
  if (dbs.length === 0)
    return item('files.geosoft_zone', 'files', title, 'qc', [], [], 'No Geosoft databases yet.');
  const survey = surveyBlocks(blocks);
  const single = expectedEpsg(survey);
  const names = files.geosoft.map((g) => g.block_folder);
  // decision 939: the zone in the block's own lines file; the block's row, then the survey's one zone, where none is read
  const wantOf = (g: GeosoftFolderFacts): number | null => {
    if (utmZoneName(g.lines_epsg) !== null) return g.lines_epsg!;
    const block = survey.find((b) => blockFolderIn(b, names) === g.block_folder);
    return block && utmZoneName(block.epsg) !== null ? block.epsg! : single;
  };
  const fails: string[] = [];
  const warns: string[] = [];
  const wants = new Set<number>();
  let right = 0;
  for (const g of files.geosoft) {
    if (g.databases.length === 0) continue;
    const epsg = wantOf(g);
    const want = utmZoneName(epsg);
    if (want === null) {
      warns.push(
        `${g.block_folder}: not checked, because its lines file gives no zone (${plural(g.databases.length, 'database')}).`,
      );
      continue;
    }
    wants.add(epsg!);
    const wrong = new Map<string, string[]>();
    const none: string[] = [];
    const unread: string[] = [];
    for (const d of g.databases) {
      if (!d.read) unread.push(d.name);
      else if (d.projection === null) none.push(d.name);
      else if (d.projection.epsg === epsg) right += 1;
      else wrong.set(d.projection.name, [...(wrong.get(d.projection.name) ?? []), d.name]);
    }
    for (const [zone, list] of wrong)
      fails.push(
        `${g.block_folder}: ${someNames(list)} ${list.length === 1 ? 'is' : `(${list.length} databases) are`} in ${zone}, but the block's lines file says ${want}. Set the coordinate system in Oasis montaj (Coordinates > Set projection on the X/Y channels) so grids and maps land in the right place.`,
      );
    if (none.length > 0)
      warns.push(
        `${g.block_folder}: ${someNames(none)} ${none.length === 1 ? 'has' : 'have'} no coordinate system; the block's lines file says ${want}.`,
      );
    if (unread.length > 0)
      warns.push(
        `${g.block_folder}: ${someNames(unread)} not checked: no .gdb.xml beside ${unread.length === 1 ? 'it' : 'them'}.`,
      );
  }
  const where = wants.size === 1 ? `in ${utmZoneName([...wants][0]!)!}` : "in their own block's zone";
  const found = item(
    'files.geosoft_zone',
    'files',
    title,
    'qc',
    fails,
    warns,
    wants.size === 0 ? 'Not checked.' : `${right} of ${plural(dbs.length, 'database')} ${where}.`,
  );
  // decision 847: the zone check stays a Fail, with "Set the zone" through the Geosoft runner; decision 939: by block
  const zoneFixes = geosoftZoneFixes(wantOf, files.geosoft);
  return zoneFixes.length > 0 ? { ...found, zoneFixes } : found;
};

/**
 * R146, R148: each block's Geosoft Scripts, Views and IO templates hold Teslyn's, each script in the zone of that
 * block's lines file (geosoft-block-templates.ts holds the rule for a file already in a block).
 */
export const checkGeosoftTemplates: Check = ({ geosoftTemplates }) => {
  const title = 'Geosoft scripts, views and IO templates in each block';
  if (geosoftTemplates === null || geosoftTemplates === undefined)
    return item('files.geosoft_templates', 'files', title, 'qc', [], [FIELD_ONLY], 'Not checked.');
  return {
    ...item(
      'files.geosoft_templates',
      'files',
      title,
      // no template to apply: the button opens where it is uploaded, not a screen that cannot help
      geosoftTemplates.noTemplate ? 'cloud.geosoft_templates' : 'qc',
      [],
      [...geosoftTemplates.problems],
      geosoftTemplates.found,
    ),
    blockTemplates: geosoftTemplates,
  };
};

// --------------------------------------------------------------------------------------------------------- the list

/** Every check in the order the checklist shows them (spec §3). */
export const QUICK_START_CHECKS: readonly {
  readonly id: ChecklistItemId;
  readonly section: ChecklistSection;
  readonly check: Check;
}[] = [
  { id: 'project.zone', section: 'project', check: checkProjectionZone },
  { id: 'project.heights', section: 'project', check: checkHeightSpecs },
  { id: 'project.qc_settings', section: 'project', check: checkQcSettings },
  { id: 'staff.register', section: 'staff', check: checkStaffRegister },
  { id: 'staff.crew', section: 'staff', check: checkCrewToday },
  { id: 'aircraft.register', section: 'aircraft', check: checkAircraft },
  { id: 'aircraft.tracker', section: 'aircraft', check: checkTrackers },
  { id: 'basemag.position', section: 'basemag', check: checkBaseMagPosition },
  { id: 'basemag.station', section: 'basemag', check: checkStationLinked },
  { id: 'basemag.diurnal', section: 'basemag', check: checkDiurnalRunning },
  { id: 'alerts.operators', section: 'alerts', check: checkAlertOperators },
  { id: 'alerts.push', section: 'alerts', check: checkAlertPush },
  { id: 'files.flight_plan', section: 'files', check: checkFlightPlan },
  { id: 'files.boundaries', section: 'files', check: checkBoundaries },
  { id: 'files.geosoft', section: 'files', check: checkGeosoftProject },
  { id: 'files.geosoft_databases', section: 'files', check: checkGeosoftDatabases },
  { id: 'files.geosoft_zone', section: 'files', check: checkGeosoftZone },
  { id: 'files.geosoft_templates', section: 'files', check: checkGeosoftTemplates },
];

export const CHECKLIST_SECTIONS: readonly ChecklistSection[] = [
  'project',
  'staff',
  'aircraft',
  'basemag',
  'alerts',
  'files',
];

/** The whole checklist, or only some sections (a wizard step). */
export function quickStartChecklist(
  input: ChecklistInput,
  sections: readonly ChecklistSection[] = CHECKLIST_SECTIONS,
): ChecklistItem[] {
  return QUICK_START_CHECKS.filter((c) => sections.includes(c.section)).map((c) => c.check(input));
}

export interface ChecklistSummary {
  readonly pass: number;
  readonly warn: number;
  readonly fail: number;
  /** The worst state. */
  readonly state: ChecklistState;
  /** "2 fail, 4 warnings, 10 pass", or "all 16 checks pass". */
  readonly text: string;
}

export function checklistSummary(items: readonly ChecklistItem[]): ChecklistSummary {
  const n = (s: ChecklistState): number => items.filter((i) => i.state === s).length;
  const pass = n('pass');
  const warn = n('warn');
  const fail = n('fail');
  const state: ChecklistState = fail > 0 ? 'fail' : warn > 0 ? 'warn' : 'pass';
  const text =
    fail === 0 && warn === 0
      ? `all ${items.length} checks pass`
      : [
          fail > 0 ? `${fail} fail` : null,
          warn > 0 ? plural(warn, 'warning') : null,
          pass > 0 ? `${pass} pass` : null,
        ]
          .filter((p): p is string => p !== null)
          .join(', ');
  return { pass, warn, fail, state, text };
}
